import { useState, useRef, useEffect } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Prediction } from "@/hooks/useGooglePlaces";

interface PlaceAutocompleteProps {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (prediction: Prediction) => void;
  predictions: Prediction[];
  loading: boolean;
  onSearch: (input: string) => void;
  iconColor?: string;
  invalid?: boolean;
}

const PlaceAutocomplete = ({
  placeholder,
  value,
  onChange,
  onSelect,
  predictions,
  loading,
  onSearch,
  iconColor = "text-primary",
  invalid = false,
}: PlaceAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (predictions.length > 0) {
      setOpen(true);
    }
  }, [predictions]);

  const showIcon = iconColor !== "hidden";

  return (
    <div ref={wrapperRef} className="relative">
      {showIcon && (
        <MapPin className={cn("absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 z-10", iconColor)} />
      )}
      {loading && (
        <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
      )}
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          onSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => predictions.length > 0 && setOpen(true)}
        className={cn(
          "flex h-14 w-full rounded-xl border bg-card px-4 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 transition-colors",
          showIcon ? "pl-12" : "pl-16",
          invalid
            ? "border-destructive focus-visible:ring-destructive hover:border-destructive"
            : "border-border focus-visible:ring-primary hover:border-primary/40"
        )}
      />
      {open && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-2xl shadow-[0_15px_50px_-10px_rgba(0,0,0,0.15)] max-h-60 overflow-y-auto">
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => {
                onSelect(p);
                setOpen(false);
              }}
              className="w-full text-left px-5 py-3.5 text-sm text-foreground hover:bg-primary/5 transition-colors flex items-start gap-3 first:rounded-t-2xl last:rounded-b-2xl"
            >
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <MapPin className="w-4 h-4 text-muted-foreground" />
              </div>
              <span className="flex-1 pt-1">{p.description}</span>
              {p.source === "db" && (
                <span className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full shrink-0 font-semibold">
                  Local
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {open && !loading && value.length >= 2 && predictions.length === 0 && (
        <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-2xl shadow-lg p-4 text-sm text-muted-foreground text-center">
          No matches found. Try another name.
        </div>
      )}
    </div>
  );
};

export default PlaceAutocomplete;
